import { RefObject, useState } from 'react';
import LoadingIndicator from '../loading-indicator';
import { getConfig } from '@micro-apps/env';
import useSWRMutation from 'swr/mutation';
import { defaultFetcher, slugifyAnalyticsSegment } from '@fshr/shared';
import { type SearchResultsReturn } from '@micro-apps/env';
import sanitizeHtml from 'sanitize-html';
import SearchBar from './SearchBar';
import RecentSearch from './RecentSearch';
import FeaturedContent from './FeaturedContent';
import SearchResults from './Results';
import useSWRImmutable from 'swr/immutable';
import { FeaturedContentResponse } from '@micro-apps/env';
import { useTealium } from '@fshr/context/tealium-context';
import { getPPAnalyticsValues } from '@micro-apps/shared';
import type { SearchModalPpProfile } from '../types';

export const ABORT_SEARCH_REASON = 'abort-search';

function getPreferredPartnerSearchTrackPayload({
	ppProfile,
	ppMode,
	allowAnonymousOnPP,
	tealiumEnvironment,
}: {
	ppProfile?: SearchModalPpProfile;
	ppMode?: boolean;
	allowAnonymousOnPP?: boolean;
	tealiumEnvironment: string | undefined;
}) {
	const utagData =
		typeof window !== 'undefined'
			? (window as Window & { utag_data?: Record<string, unknown> }).utag_data
			: undefined;

	const ppValues = getPPAnalyticsValues({
		ppProfile,
		defaultAaAccount: utagData?.['aa_account'],
		tealiumEnvironment,
		allowAnonymousOnPP,
		ppMode,
	});

	const loginState = ppValues.login_state ?? utagData?.['login_state'];
	const userType = ppValues.user_type ?? utagData?.['user_type'];

	return {
		aa_account: ppValues.aa_account,
		site_template: utagData?.['site_template'],
		page_language: utagData?.['page_language'],
		page_name: utagData?.['page_name'],
		property_code: ppMode ? 'preferred-partners' : utagData?.['property_code'],
		property_name: ppMode ? 'preferred-partners' : utagData?.['property_name'],
		property_region: ppMode ? 'corporate' : utagData?.['property_region'],
		user_type: userType,
		login_state: loginState,
		pp_agency: ppValues.pp_agency ?? utagData?.['pp_agency'],
		pp_id: ppValues.pp_id ?? utagData?.['pp_id'],
		user_preferred_name: '',
	};
}

const EMPTY_RESULTS: SearchResultsReturn = {
	numberOfResults: 0,
	results: {
		properties: [],
		other: [],
	},
};

export default function SearchModalContent({
	endpoint,
	abortController,
	ppProfile,
	ppMode,
	allowAnonymousOnPP,
}: {
	endpoint: string;
	abortController: RefObject<AbortController | undefined>;
	ppProfile?: SearchModalPpProfile;
	ppMode?: boolean;
	allowAnonymousOnPP?: boolean;
}) {
	const { trackLink } = useTealium();
	const [searchResults, setSearchResults] = useState<SearchResultsReturn>();
	const [searchTerm, setSearchTerm] = useState('');
	const [hasSearched, setHasSearched] = useState(false);
	const [lastSearchIcmpSlug, setLastSearchIcmpSlug] = useState<string | undefined>(undefined);

	//TODO these should eventually come from cookie or database
	const [recentSearchTerms, setRecentSearchTerms] = useState<string[]>([]);

	const { NEXT_PUBLIC_WEB_BFF_BASE_URL, FSHR_PUBLIC_TEALIUM_ENVIRONMENT } = getConfig();

	const searchFetcher = async (
		url: string,
		{ arg: { searchTerm } }: { arg: { searchTerm: string } }
	) => {
		abortController.current = new AbortController();
		return defaultFetcher(`${url}?searchTerm=${encodeURIComponent(searchTerm)}`, {
			method: 'GET',
			signal: abortController.current.signal,
		});
	};
	const {
		trigger: triggerSearch,
		isMutating: isLoading,
		reset: resetSWR,
	} = useSWRMutation(`${NEXT_PUBLIC_WEB_BFF_BASE_URL}/search/${endpoint}`, searchFetcher);

	const featuredSearchContentUrl = `${NEXT_PUBLIC_WEB_BFF_BASE_URL}/search/content/pp-featured-search`;

	const {
		data: featuredSearchContentData,
		error: featuredSearchContentError,
		isLoading: isFeaturedSearchLoading,
	} = useSWRImmutable<FeaturedContentResponse>(featuredSearchContentUrl, defaultFetcher);

	const resetSearch = () => {
		if (isLoading && abortController.current) {
			abortController.current.abort(ABORT_SEARCH_REASON);
		}
		resetSWR();
	};

	const onSearch = async (searchTerm: string) => {
		resetSearch();
		searchTerm = sanitizeHtml(searchTerm, {
			allowedTags: [],
			allowedAttributes: {},
		});
		const trimmedTerm = searchTerm?.trim();
		if (!trimmedTerm) {
			clearSearch();
			setLastSearchIcmpSlug(undefined);
		} else {
			try {
				const data = await triggerSearch({ searchTerm: trimmedTerm });
				setSearchResults(data);
				if (endpoint === 'preferredpartner') {
					const searchTermSlug = slugifyAnalyticsSegment(trimmedTerm.toLowerCase());
					setLastSearchIcmpSlug(searchTermSlug);
					trackLink('search', {
						search_term: trimmedTerm.toLowerCase(),
						...getPreferredPartnerSearchTrackPayload({
							ppProfile,
							ppMode,
							allowAnonymousOnPP,
							tealiumEnvironment: FSHR_PUBLIC_TEALIUM_ENVIRONMENT,
						}),
					});
				}
			} catch (e) {
				if (e === ABORT_SEARCH_REASON) {
					setSearchResults(undefined);
				} else {
					setSearchResults(EMPTY_RESULTS);
				}
				setLastSearchIcmpSlug(undefined);
			}
		}
	};

	const doSearch = () => {
		setHasSearched(true);
		onSearch(searchTerm);
	};
	const clearSearch = () => {
		resetSearch();
		setHasSearched(false);
		setSearchTerm('');
		setSearchResults(undefined);
		setLastSearchIcmpSlug(undefined);
	};

	const onRecentSearch = (searchTerm: string) => {
		setSearchTerm(searchTerm);
		setHasSearched(true);
		onSearch(searchTerm);
	};

	const onSearchBarChange = (newSearchTerm: string) => {
		setHasSearched(false);
		setSearchTerm(newSearchTerm);
	};

	return (
		<div className="flex h-full flex-col overflow-hidden">
			<div className="shrink-0">
				<SearchBar
					searchTerm={searchTerm}
					hasSearched={hasSearched}
					doSearch={doSearch}
					clearSearch={clearSearch}
					onChange={onSearchBarChange}
					isLoading={isLoading}
				/>
			</div>

			{isLoading || isFeaturedSearchLoading ? (
				<LoadingIndicator className="relative flex-1 min-h-0" />
			) : (
				<div className="flex min-h-[0px] flex-1 flex-col overflow-auto spacing-card-padding-y-xs spacing-stack-l">
					{!searchResults && (
						<>
							<RecentSearch searchTerms={recentSearchTerms} onSearch={onRecentSearch} />
							<FeaturedContent
								items={featuredSearchContentData?.result?.featuredContent || []}
								error={featuredSearchContentError}
								isPreferredPartner={endpoint === 'preferredpartner'}
							/>
						</>
					)}
					<SearchResults
						searchResults={searchResults}
						searchIcmpValue={
							endpoint === 'preferredpartner' && lastSearchIcmpSlug
								? `preferred_partners|search|${lastSearchIcmpSlug}`
								: undefined
						}
					/>
				</div>
			)}
		</div>
	);
}
